import React, { useCallback } from 'react';
import { useFocusEffect } from 'expo-router';
import { Platform, StyleSheet, View } from 'react-native';

import { useResponsive } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

import {
  LoyaltyOffers,
  LoyaltyPostPurchaseModal,
  usePostPurchaseModalState,
} from '../../components';
import { ScreenHeader } from '../../components/ScreenHeader';
import { useIsLoggedIn } from '../../context/AuthMachine';
import { useBraze } from '../../context/Braze';
import {
  useLogLoyaltyOnboardingModalEvents,
  useOnOnboardingModalShow,
  useOtaOnScreenFocus,
  usePendingOrder,
  useTrackLoyaltyOnboardingModal,
} from '../../hooks';
import { HomeScreenContent, HomeScreenContentV2 } from './components';
import { useOpenChatFromHomeScreen } from './hooks';

export const HomeScreen = () => {
  const logLoyaltyOnboardingModalEvents = useLogLoyaltyOnboardingModalEvents();
  const trackLoyaltyOnboardingModal = useTrackLoyaltyOnboardingModal();
  const openChatFromHomeScreen = useOpenChatFromHomeScreen();
  const postPurchaseModalState = usePostPurchaseModalState();
  const { logCustomEvent } = useBraze();
  const isLoggedIn = useIsLoggedIn();
  const { currentBreakpoint } = useResponsive();
  const pendingOrder = usePendingOrder({ requestPolicy: 'cache-and-network', withPolling: true });

  const isHomeRedesignEnabled = useFeatureFlag('CELS-5283-home-redesign-enabled');
  const isNavbarV2Enabled = useFeatureFlag('CELS-5440-order-navbar-v2-enabled');

  // ─── Derived ───────────────────────────────────────────────────────────────

  const shouldRenderScreenHeader = isNavbarV2Enabled && currentBreakpoint.isXS;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const hidePostPurchaseModal = useCallback(() => {
    // NOTE: We use a short delay on iOS because of a known issue during
    //       interactions between two modals.
    const delay = Platform.select({ ios: 1000, default: 0 });

    setTimeout(postPurchaseModalState.hideModal, delay);
  }, [postPurchaseModalState.hideModal]);

  // NOTE: Post-purchase modal should be hidden when the loyalty onboarding in-app message is shown.
  const hideOffersOnOnboardingModalShow = useOnOnboardingModalShow({
    onModalShow: hidePostPurchaseModal,
  });

  const logHomeViewedBrazeEvent = useCallback(() => {
    logCustomEvent('home.viewed', { isLoggedIn });
  }, [isLoggedIn, logCustomEvent]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useOtaOnScreenFocus();

  useFocusEffect(hideOffersOnOnboardingModalShow);
  useFocusEffect(trackLoyaltyOnboardingModal);
  useFocusEffect(logLoyaltyOnboardingModalEvents);
  useFocusEffect(openChatFromHomeScreen);
  useFocusEffect(logHomeViewedBrazeEvent);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.container}>
      {shouldRenderScreenHeader ? (
        <ScreenHeader pendingOrder={pendingOrder} rounded={false} />
      ) : null}

      {isHomeRedesignEnabled ? <HomeScreenContentV2 /> : <HomeScreenContent />}
      {isHomeRedesignEnabled ? null : <LoyaltyPostPurchaseModal />}

      <LoyaltyOffers.Modal />
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});
